<template>
  <div class="page-integration">
    <el-tabs type="card" v-model="activeName">
      <el-tab-pane label="For developers" name="first">
        <div class="guide">
          <div class="install">
            <h2>Quick start</h2>
            <h3>The ATK Player plug-in can be added to your webpages and launched from a button. To do that, you just need to first insert the following small blocks of JS code into the source code of your webpage.</h3>
            <h3>1. Place the block below into the HTML head section (anywhere inside the &lt;head&gt; tag). This imports the ATK player style and js code ready to use.</h3>
            <div class="code">
              <span class="hljs-comment">&lt;!-- import style --&gt;</span><br />
              <span class="hljs-tag">&lt;<span class="hljs-name">link</span> <span class="hljs-attr">rel</span>=<span class="hljs-string">"stylesheet"</span> <span class="hljs-attr">href</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/css/atk-player.css"</span>&gt;</span> <br />
              <span class="hljs-comment">&lt;!-- import JavaScript --&gt;</span><br />
              <span class="hljs-tag">&lt;<span class="hljs-name">script</span> <span class="hljs-attr">src</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/js/atk-player.js"</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
            </div>
            <h3>2. Place the block below into the HTML body section (anywhere inside the &lt;body&gt; tag). This prepares the ATK player button instance before it is inserted to the webpage.</h3>
            <div class="code">
              <span class="hljs-tag">{{ dataMessage.atkPlyer }}</span>
            </div>
            <h3>3. Find the Launch Code from one of your Player instances you’ve already created and place that into the code for the corresponding button on your webpage, for example:</h3>
            <p class="explainText">My Player Instance 1 (code for Button 1):</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' } "</span>&gt;Launch Player</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span> <br />
            </div>
            <p class="explainText">My Player Instance 2 (code for Button 2):</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' } "</span>&gt;Launch Player</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span> <br />
            </div>
            <p class="explainText">My Player Instance 3 (code for Button 3):</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' } "</span>&gt;Launch Player</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span> <br />
            </div>
            <h3>4. If you want to change the position of the minimized Player, just add 'position' to 'params', for example:</h3>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show', position: { mini: { right: '66px', bottom: '66px' } } } "</span>&gt;Launch Player</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span> <br />
            </div>
            <br />
            <h3>How do I find my Instance ID?</h3>
            <p class="explainText">You can find your Launch Code containing your Instance ID here:</p>
            <p class="explainText">1. From the main menu on the left, under INTEGRATION select <b>My Players</b>.</p>
            <p class="explainText">2. Find the instance you want for the Launch Button you’re programming on your site. Click Edit.</p>
            <p class="explainText">3. Copy the Launch Code. Also enter a note here for yourself about where this button and Instance will be located on your site.</p>
            <p class="explainText">Here’s an example: The string after 'instance' is your Instance ID:</p>
            <div class="code">
              <span>document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' }</span><br />
            </div>
          </div>
          <div class="adaptability">
            <h2>Browser Compatibility</h2>
            <span class="explainText">
              This plug-in can run on most commonly used browsers, such as Chrome, Firefox, Edge, Safari, and etc.
            </span>
          </div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="For SquareSpace" name="second">
        <div class="guide">
          <div class="install">
            <h2>Quick start</h2>
            <h3>ATK plug-in can be added to your webpages in the form of a button. To do that, you just need to change some settings in your SquareSpace web client.</h3>
            <h3>1. Open your website editor and click &lt;Pages&gt; option.</h3>
            <h3>2. Right click the page where you want to add the ATK plug-in to open Page Settings.</h3>
            <h3>3. Select &lt;Advanced&gt; and insert the following code in the column of &lt;PAGE HEADER CODE INJECTION&gt;. This imports the ATK player style and js code ready to use.</h3>
            <div class="code">
              <span class="hljs-comment">&lt;!-- import style --&gt;</span><br />
              <span class="hljs-tag">&lt;<span class="hljs-name">link</span> <span class="hljs-attr">rel</span>=<span class="hljs-string">"stylesheet"</span> <span class="hljs-attr">href</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/css/atk-player.css"</span>&gt;</span> <br />
              <span class="hljs-comment">&lt;!-- import JavaScript --&gt;</span><br />
              <span class="hljs-tag">&lt;<span class="hljs-name">script</span> <span class="hljs-attr">src</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/js/atk-player.js"</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
            </div>
            <h3>4. Add a code tag on the corresponding page, which reads as follows. This prepares the ATK player button instance before it is inserted to the webpage.</h3>
            <div class="code">
              <span class="hljs-tag">{{ dataMessage.atkPlyer }}</span>
            </div>
            <h3>5. Find the launch code from one of the following player instances and place that into the right position where you want the ATK play button to reside. For example:</h3>
            <p class="explainText">My Player Instance 1 (code for Button 1):</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' } "</span>&gt;Launch Player</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span> <br />
            </div>
            <p class="explainText">My Player Instance 2 (code for Button 3):</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' } "</span>&gt;Launch Player</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span> <br />
            </div>
            <p class="explainText">My Player Instance 3 (code for Button 3):</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' } "</span>&gt;Launch Player</span><span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span> <br />
            </div>
            <h3>How do I find my Instance ID?</h3>
            <p class="explainText">You can find your Launch Code containing your Instance ID here:</p>
            <p class="explainText">1. From the main menu on the left, under INTEGRATION select <b>My Players</b>.</p>
            <p class="explainText">2. Find the instance you want for the Launch Button you’re programming on your site. Click Edit.</p>
            <p class="explainText">3. Copy the Launch Code. Also enter a note here for yourself about where this button and Instance will be located on your site.</p>
            <p class="explainText">Here’s an example: The string after 'instance' is your Instance ID:</p>
            <div class="code">
              <span>document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' }</span><br />
            </div>
            <p class="explainText">If you want to use JavaScript to control buttons, you need to upgrade your SquareSpace to obtain premium features.</p>
          </div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="For Wordpress" name="third">
        <div class="guide">
          <div class="install">
            <h2>Quick start</h2>
            <h3>Different WordPress builders can have different import methods.</h3>
            <h3>If you are building a website through a host service provider, you can import it by installing a plug-in:</h3>
            <p class="explainText">You can import CSS and JS into the page by installing plug-ins such as Code Snippets.The following shows how to import using Code Snippets:</p>
            <p class="explainText">1. In the All Snippets option of Code Snippets, you can obtain cases and write them in Edit Snippets following the example:</p>
            <div class="code">
              <span>add_action( 'wp_head', function () { ?></span><br />
                <span class="codeFunction"><span class="hljs-tag">&lt;<span class="hljs-name">link</span> <span class="hljs-attr">rel</span>=<span class="hljs-string">"stylesheet"</span> <span class="hljs-attr">href</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/css/atk-player.css"</span>&gt;</span></span> <br />
                <span class="codeFunction"><span class="hljs-tag">&lt;<span class="hljs-name">script</span> <span class="hljs-attr">src</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/js/atk-player.js"</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span></span> <br />
              <span>&lt;?php } );</span>
            </div>
            <p class="explainText">2. After importing, you can add ATK player and the button to control its display in the page:</p>
            <div class="code">
              <span class="hljs-tag">{{ dataMessage.atkPlyer }}<br /></span>
            <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: 'YOUR INSTANCE ID', status: 'show' } "</span>&gt;</span>showATK<span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>
            </div>
            <h3>How do I find my Instance ID?</h3>
            <p class="explainText">You can find your Launch Code containing your Instance ID here:</p>
            <p class="explainText">1. From the main menu on the left, under INTEGRATION select <b>My Players</b>.</p>
            <p class="explainText">2. Find the instance you want for the Launch Button you’re programming on your site. Click Edit.</p>
            <p class="explainText">3. Copy the Launch Code. Also enter a note here for yourself about where this button and Instance will be located on your site.</p>
            <p class="explainText">Here’s an example: The string after 'instance' is your Instance ID:</p>
            <div class="code">
              <span>document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' }</span><br />
            </div>
            <h3>If you download and install WordPress by yourself, you can also import by coding in addition to plug-in import:</h3>
            <p class="explainText">1. You can find functions.php under the themes of wp-content in the current website, and add the following code in functions.php to import:</p>
            <div class="code">
              <span>function wpb_adding_scripts() {</span><br />
              <span class="codeFunction">wp_register_script('my_amazing_script', 'https://www.audiotestkitchen.com/player/dist/js/atk-player.js', array(), true);</span><br />
              <span class="codeFunction">wp_enqueue_script('my_amazing_script');</span><br />
              <span class="codeFunction">wp_register_style('my_amazing_style', 'https://www.audiotestkitchen.com/player/dist/css/atk-player.css', array(), true);</span><br />
              <span class="codeFunction">wp_enqueue_style('my_amazing_style');</span><br />
              <span>}</span><br />
              <span>add_action( 'wp_enqueue_scripts', 'wpb_adding_scripts' );</span><br />
            </div>
            <p class="explainText">2. Add a code tag on the corresponding page, which reads as follows:</p>
            <div class="code">
              <span class="hljs-tag">{{ dataMessage.atkPlyer }}</span>
            </div>
            <p class="explainText">You can also add a button to the page like this to display ATK player:</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">" document.querySelector('atk-player').params = { instance: 'YOUR INSTANCE ID', status: 'show' } "</span>&gt;</span>showATK<span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>
            </div>
            <p class="explainText">The relevant information of Instance ID can be obtained through the guidance in 'How do I find my instance ID?' above.</p>
            <p class="explainText">If you want to use JavaScript to control buttons, you need to define the method under the JS file of the current theme and import it.</p>
            <p class="explainText">For example, I created atkplay.js under the JS file:</p>
            <div class="code">
            <span>function showATK() {</span><br />
            <span class="codeFunction">document.querySelector('atk-player').params = { instance: 'YOUR INSTANCE ID', status: 'show' }</span><br />
            <span>}</span><br />
            </div>
            <p class="explainText">Then import it in functions.php:</p>
            <div class="code">
              <span>function wpb_js() {</span><br />
              <span class="codeFunction">wp_enqueue_script('showATK', get_theme_file_uri( '/assets/js/atkplay.js' ), array(),false,true);</span><br />
              <span>}</span><br />
              <span>add_action( 'wp_enqueue_scripts', 'wpb_js' );</span><br />
            </div>
            <p class="explainText">Finally, you can add buttons to the page for control:</p>
            <div class="code">
              <span class="hljs-tag">&lt;<span class="hljs-name">button</span> <span class="hljs-attr">onclick</span>=<span class="hljs-string">"showATK"</span>&gt;</span>showATK<span class="hljs-tag">&lt;/<span class="hljs-name">button</span>&gt;</span>
            </div>
          </div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="For Shopify" name="shopify">
        <div class="guide">
          <div class="install">
            <h2>Quick start</h2>
            <h3>The ATK Player plug-in can be added to Shopify with only several steps. Here is an example to add Launch Player button into mail product page.</h3>
            <h3>1. Place the block below into "layout/theme.liquid file" head section (anywhere inside the &lt;head&gt; tag). This imports the ATK player style and js code ready to use.</h3>
            <div class="code">
              <span class="hljs-comment">&lt;!-- import style --&gt;</span><br />
              <span class="hljs-tag">&lt;<span class="hljs-name">link</span> <span class="hljs-attr">rel</span>=<span class="hljs-string">"stylesheet"</span> <span class="hljs-attr">href</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/css/atk-player.css"</span>&gt;</span> <br />
              <span class="hljs-comment">&lt;!-- import JavaScript --&gt;</span><br />
              <span class="hljs-tag">&lt;<span class="hljs-name">script</span> <span class="hljs-attr">src</span>=<span class="hljs-string">"https://www.audiotestkitchen.com/player/dist/js/atk-player.js"</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
            </div>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_theme_1.png" alt="">
            </div>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_theme_2.png" alt="">
            </div>
            <h3>2. Set Metafields for Products. </h3>
            <p class="explainText">2.1 Get into "Settings(lower left quarter) > Metafields > Products", click "Add definition"</p>
            <div class="code">
              <span class="hljs-comment">&lt;-- Name --&gt;</span><br />
              <span class="hljs-tag">ATK Player Instance Id</span><br />
              <br />
              <span class="hljs-comment">&lt;-- Namespace and key  --&gt;</span><br />
              <span class="hljs-tag">atk_player.instance_id</span><br />
              <br />
              <span class="hljs-comment">&lt;-- Select content type  --&gt;</span><br />
              <span class="hljs-tag">Text(Single line text)</span><br />
            </div>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_metafield_1.png" alt="">
            </div>
            <p class="explainText">2.2 Set ATK Player instance id for products. This step can also be done later.</p>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_metafield_2.png" alt="">
            </div>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_metafield_3.png" alt="">
            </div>
            <h3>3. Add Launch Player button.</h3>
            <p class="explainText">3.1 Get into "Online Store" and click "Customize".</p>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_liquid_1.png" alt="">
            </div>
            <p class="explainText">3.2 Choose where you want to add the button, click "Add block" and selct "Custom liquid".</p>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_liquid_2.png" alt="">
            </div>
            <p class="explainText">3.3 Paste below codes into Custom liquid, click 'Save'.</p>
            <div class="code">
              <span class="hljs-comment">&lt;style&gt;</span><br />
              <span class="hljs-comment">.atk-player-section{</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;display: flex;</span><br />
              <span class="hljs-comment">}</span><br />
              <span class="hljs-comment">.atk-player-btn {</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;width: 300px;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;height: 45px;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;padding: 0;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;margin: auto;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;line-height: 45px;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;border: solid 1px rgb(18, 18, 18);</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;color: rgb(18, 18, 18);</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;font-size: 15px;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;background-color: white;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;cursor: pointer;</span><br />
              <span class="hljs-comment">}</span><br />
              <span class="hljs-comment">&lt;/style&gt;</span><br />
              <span class="hljs-comment">&lt;atk-player&gt;&lt;/atk-player&gt;</span><br />
              <span class="hljs-comment">&lt;div class="atk-player-section"&gt;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;{% if product.metafields.atk_player.instance_id %}</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;&nbsp;&nbsp;&lt;button class="atk-player-btn" onclick="document.querySelector('atk-player').params = { instance: '</span>
              <span class="hljs-comment">{</span>
              <span class="hljs-comment">{ </span>
              <span class="hljs-comment"> product.metafields.atk_player.instance_id }}', status: 'show' }"&gt;Launch Player&lt;/button&gt;</span><br />
              <span class="hljs-comment">&nbsp;&nbsp;{% endif %}</span><br />
              <span class="hljs-comment">&lt;/div&gt;</span><br />
              <!-- <div class="hljs-comment">{{ codes }}</div> -->
            </div>
            <div class="desc-img">
              <img src="@/assets/images/integration/shopify/step_liquid_3.png" alt="">
            </div>
            <h3>How do I find my Instance ID?</h3>
            <p class="explainText">You can find your Launch Code containing your Instance ID here:</p>
            <p class="explainText">1. From the main menu on the left, under INTEGRATION select <b>My Players</b>.</p>
            <p class="explainText">2. Find the instance you want for the Launch Button you’re programming on your site. Click Edit.</p>
            <p class="explainText">3. Copy the Launch Code. Also enter a note here for yourself about where this button and Instance will be located on your site.</p>
            <p class="explainText">Here’s an example: The string after 'instance' is your Instance ID:</p>
            <div class="code">
              <span>document.querySelector('atk-player').params = { instance: '6157362af2a32c6cd513eef2', status: 'show' }</span><br />
            </div>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>
<script>
export default {
  name: 'Integration',
  data () {
    return {
      activeName: 'first',
      dataMessage: {
        atkPlyer: '<atk-player />'
      },
      codes: `
        <style>\n
          .atk-player-section {
            display: flex;
          }
          .atk-player-btn {
            width: 300px;
            height: 45px;
            padding: 0;
            margin: auto;
            line-height: 45px;
            border: solid 1px rgb(18, 18, 18);
            color: rgb(18, 18, 18);
            font-size: 15px;
            background-color: white;
            cursor: pointer;
          }
        </style>
        <atk-player></atk-player>
        <div class="atk-player-section">
          {% if product.metafields.atk_player.instance_id %}
            <button class="atk-player-btn" onclick="document.querySelector('atk-player').params = { instance: '{{ product.metafields.atk_player.instance_id }}', status: 'show' }">Launch Player</button>
          {% endif %}
        </div>\
      `
    }
  }
}
</script>
<style lang="scss" scoped>
.guide {
    padding: 10px 10px 30px;
    h3 {
      font-size: 18px;
      padding-bottom: 3px;
    }
    .code {
      width: 100%;
      font-size: 14px;
      background-color: #fafafa;
      border: 1px solid #eaeefb;
      border-radius: 4px;
      padding: 18px 24px;
      line-height: 1.7em;
      .hljs-tag {
        color: #3182BD;
      }
      .hljs-string {
        color: #756bb1;
      }
      span.codeFunction {
        padding-left: 2em;
      }
    }
    .desc-img {
      padding: 20px;
      background-color: #FAFAFA;
      border-radius: 4px;
      margin: 20px auto;
      img {
        width: 100%;
      }
    }
    .explainText {
      font-size: 16px;
      padding-bottom: 3px;
      .codepan:visited {
        color: #409EEF;
      }
    }
}
</style>
